/**
 * VoiceNoteRecorder — hold a button and talk, instead of finding a file.
 *
 * Owner decision D2 (2026-10-07): "Ideally support in-browser recording and
 * upload (currently upload only)."  Upload stays exactly as it was; this is
 * the second way in.
 *
 * Deliberate constraints (REFACTOR_PLAN.md PR-9 and §F.21 "do not break the
 * monolith"):
 *
 *   - **No new dependency.**  The browser's own `MediaRecorder` records; there
 *     is no audio library, no worker, no wasm encoder.
 *   - **No new backend path.**  The recording is handed to the EXISTING
 *     `POST /api/teaching-videos/upload` as a file, so it goes through the same
 *     MIME allow-list, the same duration gate, the same R2 dual-write and the
 *     same extraction as a file a coach picked from disk.  A voice note and an
 *     uploaded clip are the same object from the first second onward.
 *   - **The duration cap is READ, not invented.**  `maxSeconds` defaults to
 *     `MAX_TEACHING_VIDEO_DURATION_SECONDS`, the mirror of the backend's
 *     `SPORTFORM_MAX_TEACHING_VIDEO_DURATION`.  The recorder stops itself there
 *     rather than letting a coach talk for ten minutes and then be refused by
 *     the server — and it KEEPS what they said, because the first two minutes
 *     of a coach explaining a movement are not disposable.
 *
 * Every string answers what / why / how per docs/COACH_FACING_COPY_STANDARD.md
 * §1 and avoids the §2 vocabulary (no "MediaRecorder", no MIME types, no
 * "stream", no file names).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from '@/components/ui';
import { describeDurationInWords, formatDuration } from '@/utils/formatters';
import { useUploadTeachingVideo } from '@/hooks/useTeachingVideo';
import { MAX_TEACHING_VIDEO_DURATION_SECONDS } from '@/utils/constants';

/**
 * idle      — nothing recorded yet (also the state after a cancel)
 * recording — the microphone is live; elapsed time is shown
 * capped     — we stopped at the cap; the coach chooses to use it or start again
 * uploading — sending it to the existing upload endpoint
 * done      — uploaded; the parent has been told the id
 * denied    — the browser refused microphone access
 * unsupported — this browser cannot record
 * failed    — recording or upload failed
 */
type RecorderState =
  | 'idle'
  | 'recording'
  | 'capped'
  | 'uploading'
  | 'done'
  | 'denied'
  | 'unsupported'
  | 'failed';

export interface VoiceNoteRecorderProps {
  sport: string;
  movement: string;
  /** Called with the new teaching-video id once the recording is uploaded. */
  onUploaded: (teachingVideoId: string) => void;
  /** Seconds we can read. Defaults to the backend's teaching-video cap. */
  maxSeconds?: number;
}

/** Candidate recording formats, in preference order.
 *
 * Both are in the backend's `_ACCEPTED_AUDIO_MIMES`, so whichever the browser
 * supports is one the upload endpoint already accepts.  Safari records
 * `audio/mp4`; Chrome and Firefox record `audio/webm`.
 */
const RECORDING_FORMATS: { mimeType: string; extension: string }[] = [
  { mimeType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/mp4', extension: 'm4a' },
];

function pickFormat(): { mimeType: string; extension: string } | null {
  const Recorder = typeof window === 'undefined' ? undefined : window.MediaRecorder;
  if (!Recorder) return null;
  for (const candidate of RECORDING_FORMATS) {
    // isTypeSupported is optional in older implementations; if it is missing we
    // take the first candidate and let the recorder fall back to its default.
    if (typeof Recorder.isTypeSupported !== 'function') return candidate;
    if (Recorder.isTypeSupported(candidate.mimeType)) return candidate;
  }
  return null;
}

function canRecord(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.MediaRecorder) return false;
  return Boolean(navigator.mediaDevices?.getUserMedia);
}

export default function VoiceNoteRecorder({
  sport,
  movement,
  onUploaded,
  maxSeconds = MAX_TEACHING_VIDEO_DURATION_SECONDS,
}: VoiceNoteRecorderProps) {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recordedRef = useRef<File | null>(null);
  /** Set when the coach cancels, so the stop handler discards instead of keeping. */
  const cancelledRef = useRef(false);
  /** Set when WE stopped at the cap, so the stop handler keeps and asks.
   *
   * A ref, not state: `recorder.onstop` is the closure created in
   * startRecording(), so a `state` read inside it is the value state had when
   * recording began ("recording") and never the cap. */
  const cappedRef = useRef(false);

  const uploadMutation = useUploadTeachingVideo();

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /** Release the microphone. Always paired with leaving the recording state. */
  const releaseMicrophone = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  // Never leave the microphone live when this component goes away.
  useEffect(() => {
    return () => {
      clearTimer();
      try {
        recorderRef.current?.stop();
      } catch {
        // Already stopped; nothing to do.
      }
      releaseMicrophone();
    };
  }, [clearTimer, releaseMicrophone]);

  const upload = useCallback(
    async (file: File) => {
      setState('uploading');
      setNotice(null);
      try {
        const response = await uploadMutation.mutateAsync({ file, sport, movement });
        setState('done');
        onUploaded(response.id);
      } catch (err) {
        setState('failed');
        // A server refusal (too long, wrong kind of file, over quota) already
        // arrives as a sentence written for the coach, so it is shown as-is;
        // anything else gets our own line.
        setNotice(
          err instanceof Error && err.message
            ? err.message
            : 'We could not send your recording. It is still here, so you can ' +
              'send it again, or record a new one.',
        );
      }
    },
    [movement, onUploaded, sport, uploadMutation],
  );

  const handleStopped = useCallback(
    (format: { mimeType: string; extension: string }) => {
      clearTimer();
      releaseMicrophone();

      if (cancelledRef.current) {
        cancelledRef.current = false;
        chunksRef.current = [];
        recordedRef.current = null;
        setElapsed(0);
        setState('idle');
        setNotice(
          'Recording discarded. Nothing was sent. Press record when you want ' +
            'to start again.',
        );
        return;
      }

      const blob = new Blob(chunksRef.current, { type: format.mimeType });
      chunksRef.current = [];
      const file = new File([blob], `voice-note.${format.extension}`, {
        type: format.mimeType,
      });
      recordedRef.current = file;

      // The cap path stops the recording itself and asks before sending, so
      // the coach can decide whether the part we kept is worth analysing.
      if (cappedRef.current) return;
      void upload(file);
    },
    [clearTimer, releaseMicrophone, upload],
  );

  const startRecording = useCallback(async () => {
    setNotice(null);
    if (!canRecord()) {
      setState('unsupported');
      setNotice(
        'This browser cannot record sound. Record on your phone instead and ' +
          'upload the file here, or type your notes.',
      );
      return;
    }
    const format = pickFormat();
    if (!format) {
      setState('unsupported');
      setNotice(
        'This browser cannot record sound in a form we can read. Record on ' +
          'your phone instead and upload the file here, or type your notes.',
      );
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // Every failure here is, from the coach's side, the same thing: we did
      // not get the microphone. The browser's own error names are not useful
      // to them, and the instruction is the same.
      setState('denied');
      setNotice(
        'We do not have permission to use your microphone, so we cannot ' +
          'record. Allow microphone access for this site in your browser, ' +
          'then press record again — or upload a file instead.',
      );
      return;
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: format.mimeType });
    } catch {
      stream.getTracks().forEach((track) => track.stop());
      setState('failed');
      setNotice(
        'We could not start recording. Try again, or upload a file instead.',
      );
      return;
    }

    cancelledRef.current = false;
    cappedRef.current = false;
    chunksRef.current = [];
    recordedRef.current = null;
    streamRef.current = stream;
    recorderRef.current = recorder;

    recorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) chunksRef.current.push(event.data);
    };
    recorder.onstop = () => handleStopped(format);
    recorder.onerror = () => {
      clearTimer();
      releaseMicrophone();
      setState('failed');
      setNotice(
        'The recording stopped unexpectedly. Press record to try again, or ' +
          'upload a file instead.',
      );
    };

    recorder.start();
    setElapsed(0);
    setState('recording');

    // The interval ONLY counts. The cap is enforced in the effect below,
    // because a state updater must be pure — React calls it twice in
    // StrictMode, and stopping a recorder inside one is a side effect.
    timerRef.current = setInterval(() => {
      setElapsed((previous) => previous + 1);
    }, 1000);
  }, [clearTimer, handleStopped, releaseMicrophone]);

  // The duration cap: stop ourselves, KEEP what was said, and ask. Reaching
  // the cap is not an error — a coach who talked for the full two minutes said
  // two minutes of useful things.
  useEffect(() => {
    if (state !== 'recording') return;
    if (elapsed < Math.round(maxSeconds)) return;
    clearTimer();
    cappedRef.current = true;
    setState('capped');
    setNotice(
      `We stopped the recording at ${describeDurationInWords(maxSeconds)}, because ` +
        'that is as much as we can listen to at once. You can send what you ' +
        'recorded, or start again and keep it shorter.',
    );
    try {
      recorderRef.current?.stop();
    } catch {
      // Already stopped.
    }
  }, [clearTimer, elapsed, maxSeconds, state]);

  const stopAndSend = useCallback(() => {
    cancelledRef.current = false;
    clearTimer();
    try {
      recorderRef.current?.stop();
    } catch {
      setState('failed');
      setNotice('We could not finish the recording. Please try again.');
    }
  }, [clearTimer]);

  const cancelRecording = useCallback(() => {
    cancelledRef.current = true;
    clearTimer();
    try {
      recorderRef.current?.stop();
    } catch {
      // Nothing recording — just reset.
      releaseMicrophone();
      setElapsed(0);
      setState('idle');
      setNotice('Recording discarded. Nothing was sent.');
    }
  }, [clearTimer, releaseMicrophone]);

  const sendCappedRecording = useCallback(() => {
    cappedRef.current = false;
    const file = recordedRef.current;
    if (!file) {
      setState('idle');
      return;
    }
    void upload(file);
  }, [upload]);

  const restart = useCallback(() => {
    recordedRef.current = null;
    cappedRef.current = false;
    setElapsed(0);
    setNotice(null);
    setState('idle');
  }, []);

  const remaining = Math.max(0, Math.round(maxSeconds) - elapsed);

  return (
    <div className="space-y-3" data-testid="voice-note-recorder">
      {state === 'idle' && (
        <div className="space-y-2">
          <p className="text-sm text-text-secondary">
            Talk through the movement and we will turn what you say into
            suggested checks. Say the phases, the cues you use, and the mistakes
            you watch for.
          </p>
          <Button variant="primary" onClick={startRecording} className="w-full sm:w-auto">
            Record a voice note
          </Button>
          <p className="text-xs text-text-muted">
            Keep it under {describeDurationInWords(maxSeconds)}. Find somewhere quiet and
            speak as if you were coaching one athlete.
          </p>
        </div>
      )}

      {state === 'recording' && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-2.5 w-2.5 rounded-full bg-danger animate-pulse"
            />
            <p className="text-sm font-medium text-text-primary">
              Recording — {formatDuration(elapsed)}
            </p>
          </div>
          <p className="text-xs text-text-muted">
            {remaining > 0
              ? `${formatDuration(remaining)} left. Press stop when you have said everything.`
              : 'Press stop when you have said everything.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={stopAndSend}
              className="w-full sm:w-auto"
            >
              Stop and use this
            </Button>
            <Button
              variant="outline"
              onClick={cancelRecording}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {state === 'capped' && (
        <div className="space-y-2">
          {notice && <Alert variant="warning">{notice}</Alert>}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary"
              onClick={sendCappedRecording}
              className="w-full sm:w-auto"
            >
              Use this recording
            </Button>
            <Button variant="outline" onClick={restart} className="w-full sm:w-auto">
              Start again
            </Button>
          </div>
        </div>
      )}

      {state === 'uploading' && (
        <div className="flex items-center gap-3 py-2">
          <Spinner size="sm" />
          <p className="text-sm text-text-primary">
            Sending your voice note. This takes a few seconds — stay on this
            page.
          </p>
        </div>
      )}

      {state === 'done' && (
        <Alert variant="success">
          We have your voice note and we are reading it now. The checks we find
          will appear below when it is finished.
        </Alert>
      )}

      {(state === 'denied' || state === 'unsupported' || state === 'failed') && (
        <div className="space-y-2">
          {notice && <Alert variant="error">{notice}</Alert>}
          <div className="flex flex-wrap gap-2">
            {state === 'failed' && recordedRef.current && (
              <Button
                variant="primary"
                onClick={sendCappedRecording}
                className="w-full sm:w-auto"
              >
                Send it again
              </Button>
            )}
            <Button
              variant="outline"
              onClick={startRecording}
              className="w-full sm:w-auto"
            >
              {state === 'failed' && recordedRef.current
                ? 'Record a new note'
                : 'Try recording again'}
            </Button>
          </div>
        </div>
      )}

      {state === 'idle' && notice && <Alert variant="info">{notice}</Alert>}
    </div>
  );
}
